import { useState, useEffect, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Check, X, Undo2, Banknote } from "lucide-react";
import { toast } from "sonner";
import { StatusBadge, PaymentBadge, RefundBadge } from "@/pages/MyBookings";
import { useLanguage } from "@/contexts/LanguageContext";


export function RefundActions({ item, onDone }) {
  const { t } = useLanguage();
  const run = async (url, msg) => {
    try { await api.put(url); toast.success(msg); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (item.refund_status === "pending") {
    return (
      <Button size="sm" className="rounded-full h-7 text-xs bg-golden hover:bg-golden/90 text-black gap-1" onClick={() => run(`/admin/bookings/${item._id}/mark-refunded`, t("ab.refund_done"))} data-testid={`booking-mark-refunded-${item._id}`}>
        <Banknote size={12} /> {t("ab.refunded")}
      </Button>
    );
  }
  if (item.refund_status === "refunded") {
    return (
      <Button size="sm" variant="ghost" className="rounded-full h-7 text-xs gap-1 text-muted-foreground" onClick={() => run(`/admin/bookings/${item._id}/mark-refund-pending`, t("ab.refund_pending"))} data-testid={`booking-undo-refund-${item._id}`}>
        <Undo2 size={12} /> {t("ab.undo_refund")}
      </Button>
    );
  }
  return <span className="text-xs text-muted-foreground">—</span>;
}

export function PaymentActions({ item, paidUrl, unpaidUrl, cancelUrl, onDone, testPrefix }) {
  const { t } = useLanguage();
  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (item.status === "cancelled") return <div className="flex justify-end"><RefundActions item={item} onDone={onDone} /></div>;
  return (
    <div className="flex items-center justify-end gap-1">
      {item.payment_status !== "paid" ? (
        <Button size="sm" className="rounded-full h-7 text-xs bg-green-600 hover:bg-green-700 text-white gap-1" onClick={() => run(() => api.put(paidUrl), t("ab.marked_paid"))} data-testid={`${testPrefix}-mark-paid-${item._id}`}><Check size={12} /> {t("ab.paid")}</Button>
      ) : unpaidUrl && (
        <Button size="sm" variant="ghost" className="rounded-full h-7 text-xs gap-1 text-muted-foreground" onClick={() => run(() => api.put(unpaidUrl), t("ab.marked_unpaid"))} data-testid={`${testPrefix}-mark-unpaid-${item._id}`}><Undo2 size={12} /> {t("ab.unpaid_btn")}</Button>
      )}
      <Button size="sm" variant="ghost" className="rounded-full h-7 text-xs text-destructive gap-1" onClick={() => { if (window.confirm(t("ab.cancel_confirm"))) run(() => api.put(cancelUrl), t("ab.cancelled")); }} data-testid={`${testPrefix}-cancel-${item._id}`}><X size={12} /></Button>
    </div>
  );
}

export default function AdminBookings({ onChanged }) {
  const { t } = useLanguage();
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("upcoming");

  const load = useCallback(() => { api.get("/admin/bookings").then(r => setBookings(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const done = () => { load(); onChanged?.(); };

  const today = new Date().toISOString().slice(0, 10);
  const shown = bookings
    .filter(b => filter === "all" ? true : filter === "unpaid" ? (b.payment_status !== "paid" && b.status !== "cancelled") : filter === "refunds" ? !!b.refund_status : (b.date >= today && b.status !== "cancelled"))
    .sort((a, b) => filter === "all" || filter === "refunds" ? (b.cancelled_at || b.created_at || "").localeCompare(a.cancelled_at || a.created_at || "") : (a.date + a.start_time).localeCompare(b.date + b.start_time));
  const refundsDue = bookings.filter(b => b.refund_status === "pending").length;

  return (
    <Card className="border-border/50" data-testid="admin-bookings">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-lg">{t("ab.title")} ({shown.length})</CardTitle>
        <div className="flex gap-1 bg-muted/40 rounded-full p-1">
          {[["upcoming", t("ab.upcoming")], ["unpaid", t("ab.unpaid")], ["refunds", refundsDue ? t("ab.refunds_due", { n: refundsDue }) : t("ab.refunds")], ["all", t("ab.all")]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} className={`px-3 py-1 rounded-full text-xs font-medium ${filter === v ? "bg-coral text-white" : v === "refunds" && refundsDue ? "text-golden" : "text-muted-foreground"}`} data-testid={`bookings-filter-${v}`}>{l}</button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("ab.date")}</TableHead><TableHead>{t("ab.time")}</TableHead><TableHead>{t("ab.by")}</TableHead><TableHead>{t("ab.ref")}</TableHead>
              <TableHead>{t("ab.payment")}</TableHead><TableHead>{t("ab.status")}</TableHead><TableHead>{t("ab.amount")}</TableHead><TableHead className="text-right">{t("ab.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.map(b => (
              <TableRow key={b._id} data-testid="admin-booking-row">
                <TableCell className="text-sm whitespace-nowrap">{b.date}</TableCell>
                <TableCell className="text-sm whitespace-nowrap">{b.start_time}–{b.end_time}</TableCell>
                <TableCell className="text-sm">{b.user_name || b.player_names?.[0] || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{b.reference || "—"}</TableCell>
                <TableCell><PaymentBadge item={b} /></TableCell>
                <TableCell><div className="flex flex-wrap gap-1"><StatusBadge status={b.status} /><RefundBadge item={b} /></div></TableCell>
                <TableCell className="font-medium whitespace-nowrap">Rs {Number(b.total_price).toLocaleString()}</TableCell>
                <TableCell><PaymentActions item={b} testPrefix="booking" paidUrl={`/admin/bookings/${b._id}/mark-paid`} unpaidUrl={`/admin/bookings/${b._id}/mark-unpaid`} cancelUrl={`/admin/bookings/${b._id}/cancel`} onDone={done} /></TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-10">{t("ab.empty")}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
